import {
    Box,
    ButtonFixedFooterLayout,
    ButtonLayout,
    ButtonLink,
    ButtonPrimary,
    Callout,
    EmptyState,
    EmptyStateCard,
    IconWarningRegular,
    InfoFeedbackScreen,
    Spinner,
    Stack,
    Tag,
    Text6,
    IconInformationUserRegular,
    skinVars,
    IconInvoiceEuroRegular,
    Header,
    DataCard,
    IconImportRegular,
    useWindowSize,
    BoxedRow,
    IconInvoicePlanFileRegular,
} from '@telefonica/mistica';
import React, { useEffect, useRef, useState } from 'react';
import { formatPrices } from '../../utils/dataTransformation/unitsConversion';
import './index.scss';
import { useTranslation } from 'react-i18next';
import BillsBarChart from '../../components/BillsBarChart';
import { dateToLongMonth, dateToMonth, getFormatDate } from '../../utils/dataTransformation/dateConversions';
import { BillsMonthType, IBill, IBillsItem } from '../../models/Bills.interfaces';
import { AxiosError, AxiosResponse } from 'axios';
import { useDispatch } from 'react-redux';
import { ETypeUser } from '../../utils/store/model/store.model';
import { getBills, getDebt } from '../../services/BillService';
import { showSnackbar } from '../../utils/store/features/user';
import Stopper from '../../components/Stopper/index';
import { TrackingEvents } from '../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../utils/navigation/controlDevices';
import { managementEnvironment } from '../../utils/dataTransformation/environments';
import { billsCharsOrganize } from './utils/functions';
import { billsByMonth, billsChartSize } from './utils/constants/bills';
import useBillsHook from './utils/hooks';
import { useNavigate } from 'react-router-dom';
import useUserlight from '../../hooks/useUserLight';
import { useBillsContext } from '../../context/bills-context/BillsContext';
import BillsPageBullets from '../../components/BillsPageBullets';

const Bills: React.FC = () => {
    const isLight = useUserlight();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const downloadRef = useRef<HTMLAnchorElement>(null);
    const { bills: billsContext, setBills } = useBillsContext();
    const { sendToNative, downloadPdf } = useBillsHook();
    const typeUser = new URLSearchParams(window.location.search).get('clientType');
    const billsDisabled = managementEnvironment().VITE_STOPPER_BILLS_DISABLED;
    const { trackingScreenView } = new TrackingEvents();
    const [filterBills, setfilterBills] = useState<IBill[]>([]);
    const size = useWindowSize();
    const rates: { [key: string]: string } = {
        LNF: t('fiberAndMobileBill'),
        LNM: t('mobileLineBill'),
    };
    const [currentDates, setCurrentDates] = useState<number[]>([]);
    const [debt, setDebt] = useState(true);
    const [dataRes, setDataRes] = useState<any>(null);
    const [dataResDivided, setDataResDivided] = useState<any>(null);
    const [charSlide, setCharSlide] = useState(2);
    const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');

    const { trackingUserInteraction } = new TrackingEvents();

    const numberOfSlides = Math.max(Math.floor((dataRes?.length || 0) / billsChartSize), 0);
    const emptyFallback = Array.from({ length: numberOfSlides }, () => []);

    const checkDebt = () => {
        getInfoBills();
        if (debt) {
            sendToNative('/modal-debt-payment', 'modeModal-backgroundBlue', t('debtPaymentModalHeader'));
        } else {
            return (
                <InfoFeedbackScreen
                    title={t('checkDebtTitle')}
                    description={t('checkDebtSubtitle')}
                    primaryButton={
                        <ButtonFixedFooterLayout>
                            <ButtonLayout
                                align='full-width'
                                primaryButton={
                                    <ButtonPrimary onPress={() => {}}>{t('buttonUnderstoodFeedback')}</ButtonPrimary>
                                }
                            />
                        </ButtonFixedFooterLayout>
                    }
                ></InfoFeedbackScreen>
            );
        }
    };

    useEffect(() => {
        if (!billsDisabled) getInfoBills();
        if (!(isWebviewAndroid && isWebviewIOS)) {
            trackingScreenView();
        }
    }, []);

    useEffect(() => {
        if (dataRes) setDataResDivided(billsCharsOrganize(dataRes));
    }, [dataRes]);

    const formatMonthAndYearDate = (date: Date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${year}`;
    };

    const getMonthsAndYears = (data: IBill[]): string[] => {
        const NUMBERS_BILLS = 18;

        const currentDate = new Date();

        const months: string[] = Array.from({ length: NUMBERS_BILLS }, (_, index) => {
            const date = new Date(currentDate);
            date.setMonth(date.getMonth() - index);
            return formatMonthAndYearDate(date); // Assuming this function formats as needed
        });

        return months.reverse();
    };

    const handleDates = (data: IBill[]) => {
        let dateBillsMonths: BillsMonthType[] = [];
        let tempDates: number[] = [];

        const recentMonths = getMonthsAndYears(data);

        recentMonths.forEach((monthAndYear, index) => {
            const monthBills = getBillsPerMonthYear(data, monthAndYear, index);

            dateBillsMonths.push(monthBills);
        });

        for (let i = 0; i < dateBillsMonths.length / billsChartSize; i++) {
            tempDates.push(dateBillsMonths.length / (dateBillsMonths.length / billsChartSize) - 1);
        }

        setDataRes(dateBillsMonths);
        setCurrentDates(tempDates);
        return dateBillsMonths;
    };

    const getBillsPerMonthYear = (bills: IBill[], monthAndYear: string, index: number) => {
        let count = 0;
        let debt = 0;
        let amount = 0;
        let monthItem = '';

        const billsThisMonth = bills.filter((bill) => {
            const date = new Date(bill.issueDate);
            return formatMonthAndYearDate(date) === monthAndYear;
        });

        billsThisMonth.forEach((bill) => {
            count += bill.amount;
            debt += bill.debt ?? 0;
            amount = bill.amount;
            monthItem = bill.issueDate.toString();
        });

        return {
            count,
            debt,
            amount,
            active: currentDates.includes(index),
            payment: amount < 0,
            monthItem,
            month: dateToLongMonth(monthAndYear.slice(0, 2)),
            monthSort: dateToMonth(monthAndYear.slice(0, 2)),
            year: monthAndYear.slice(3),
        };
    };

    const onHandleMonthActive = (date: IBillsItem, index: number) => {
        const { monthItem } = date;
        const update = dataRes?.map((item: IBillsItem, i: number) => {
            if (new Date(monthItem).getMonth() === new Date(item.issueDate).getMonth()) {
                return {
                    ...item,
                    active: true,
                };
            }
            return {
                ...item,
                active: false,
            };
        });

        const filterBillsUpdate = billsContext.flat().filter((item: IBill) => {
            const itemDate = new Date(item.issueDate);
            const monthDate = new Date(dataResDivided[charSlide][index].monthItem);

            return itemDate.getMonth() === monthDate.getMonth() && itemDate.getFullYear() === monthDate.getFullYear();
        });

        const tempCurrentDates = [...currentDates];
        tempCurrentDates[charSlide] = index;

        setDataRes(update);
        setCurrentDates(tempCurrentDates);
        setfilterBills(dataResDivided[charSlide][index].count > 0 ? filterBillsUpdate : []);
    };

    const refreshStatusDebtPayment = () => {
        getInfoBills();
    };

    let [loaded, setLoaded] = useState(false);

    const getInfoBills = async () => {
        await Promise.all([getBills(billsByMonth), getDebt()])
            .then((response: AxiosResponse[]) => {
                const [bills, debt] = response;

                const resp = handleDates(bills.data);
                const filterBillsUpdate = bills.data.filter((item: IBillsItem) => {
                    const itemDate = new Date(item.issueDate);
                    const monthDate = new Date(resp[resp.length - 1].monthItem);

                    return (
                        itemDate.getMonth() === monthDate.getMonth() &&
                        itemDate.getFullYear() === monthDate.getFullYear()
                    );
                });

                setDebt(debt.data);
                setBills(bills.data);
                setfilterBills(filterBillsUpdate);
                setLoaded(true);
            })
            .catch((error: AxiosError) => {
                console.error(error);
                setLoaded(true);
            });
    };

    const setTypeTag = (debt: number, amount: number) => {
        if (debt > 0) {
            return <Tag type='error'>{t('billPendingPayment')}</Tag>;
        }
        if (amount < 0) {
            return <Tag type='active'>{t('payment')}</Tag>;
        }
        return <Tag type='active'>{t('available')}</Tag>;
    };

    const sliderChangeHandler = (pageIndex: number) => {
        const lastPageIndex = (dataResDivided?.length ?? 1) - 1;
        if (pageIndex > lastPageIndex || pageIndex < 0) return;
        setSlideDirection(pageIndex > charSlide ? 'left' : 'right');
        setCharSlide(pageIndex);

        const filterBillsUpdate = billsContext.flat().filter((item: IBill) => {
            const itemDate = new Date(item.issueDate);
            const monthDate = new Date(dataResDivided[pageIndex][currentDates[pageIndex]].monthItem);

            return itemDate.getMonth() === monthDate.getMonth() && itemDate.getFullYear() === monthDate.getFullYear();
        });

        setfilterBills(dataResDivided[pageIndex][currentDates[pageIndex]].count > 0 ? filterBillsUpdate : []);
    };

    const arrowHandler = (isPrev: boolean) => {
        sliderChangeHandler(isPrev ? charSlide - 1 : charSlide + 1);
    };

    const downloadBillsHandler = () => {
        if (isLight) dispatch(showSnackbar());
        else navigate('/bills-download');
    };

    const claimsHistoryHandler = () => navigate('/claims/claim-list');

    const bills = () => {
        return (
            <div
                className='bills-page'
                style={{
                    flexDirection: size.width >= 1024 && billsContext.length > 0 ? 'row' : 'column',
                }}
            >
                {billsContext.length > 0 ? (
                    <>
                        <div
                            className='bills-page__header'
                            style={{
                                position: filterBills.length === 0 && size.width >= 1024 ? 'static' : 'sticky',
                            }}
                        >
                            <div className='bills-page-header-title'>
                                <Header
                                    title={formatPrices(
                                        dataResDivided && dataResDivided[charSlide][currentDates[charSlide]].count,
                                        'spent',
                                    )}
                                    description={
                                        filterBills.length > 0
                                            ? `${t('billsMonthDesc')} ${
                                                  dataResDivided &&
                                                  dataResDivided[charSlide][currentDates[charSlide]].month
                                              }`
                                            : t('billsNoSpentDesc')
                                    }
                                />
                            </div>
                            <div className='bills-page__header-barChart'>
                                <div
                                    key={charSlide}
                                    className={`bills-page__header-barChart-slide bills-page__header-barChart-slide--${slideDirection}`}
                                >
                                    <BillsBarChart
                                        active={currentDates[charSlide]}
                                        data={(dataResDivided ?? emptyFallback)[charSlide] ?? []}
                                        onHandleMonthActive={onHandleMonthActive}
                                    ></BillsBarChart>
                                </div>
                            </div>
                            <BillsPageBullets
                                debt={debt}
                                charSlide={charSlide}
                                bulletPointsClickHandler={sliderChangeHandler}
                                numPages={numberOfSlides}
                                arrowHandler={arrowHandler}
                            />
                            {debt && (
                                <div className='bills-page__header-callout'>
                                    <Callout
                                        asset={<IconWarningRegular color={skinVars.colors.error} size={24} />}
                                        description={t('calloutBillOutstanding')}
                                        button={
                                            <ButtonPrimary
                                                small
                                                onPress={() => {
                                                    checkDebt();
                                                }}
                                            >
                                                {t('calloutButtonPrimaryBillOutstanding')}
                                            </ButtonPrimary>
                                        }
                                        buttonLink={
                                            <ButtonLink
                                                onPress={() => {
                                                    refreshStatusDebtPayment();
                                                }}
                                            >
                                                {t('calloutButtonLinkPrimaryBillOutstanding')}
                                            </ButtonLink>
                                        }
                                    ></Callout>
                                </div>
                            )}
                            {size.width >= 1024 && !debt && (
                                <div className='bills-page__header-callout'>
                                    <Callout
                                        asset={<IconInformationUserRegular color={skinVars.colors.brand} size={24} />}
                                        description={t('billsCalloutDesktopBillsOK')}
                                    ></Callout>
                                </div>
                            )}
                        </div>
                        {filterBills.length === 0 && (
                            <div className='bills-page__borderEmptyState'>
                                <div className='bills-page__emptyStateMonth'>
                                    <EmptyStateCard
                                        asset={<IconInvoiceEuroRegular color={skinVars.colors.brand} size={64} />}
                                        title={t('billsEmptyStateNotAvaiableTitle')}
                                        description={t('billsEmptyStateNotAvaiableDescription')}
                                    ></EmptyStateCard>
                                </div>

                                <div className='bills-page__download'>
                                    <BoxedRow
                                        onPress={downloadBillsHandler}
                                        asset={
                                            <div
                                                className='bills-page__download-icon'
                                                style={{ backgroundColor: skinVars.colors.brandLow }}
                                            >
                                                <IconInvoicePlanFileRegular color={skinVars.colors.brand} size={24} />
                                            </div>
                                        }
                                        title={t('downloadInvoices')}
                                    />
                                </div>
                                <div className='bills-page__claim-history' style={{ marginBottom: '24px' }}>
                                    <BoxedRow
                                        onPress={claimsHistoryHandler}
                                        asset={
                                            <div
                                                className='bills-page__claim-history-icon'
                                                style={{ backgroundColor: skinVars.colors.brandLow }}
                                            >
                                                <IconInvoiceEuroRegular color={skinVars.colors.brand} size={24} />
                                            </div>
                                        }
                                        title={t('claimHistory')}
                                    />
                                </div>
                            </div>
                        )}
                        {filterBills.length > 0 && (
                            <div className='bills-page__desktopWrapper'>
                                {filterBills?.map((item: any, index: number) => {
                                    return (
                                        <div className='bills-page__desktopWrapper-listBills' key={index}>
                                            <DataCard
                                                actions={[
                                                    {
                                                        Icon: IconImportRegular,
                                                        onPress: () => {
                                                            trackingUserInteraction('download_bills_tapped');
                                                            downloadPdf(item.id, downloadRef);
                                                        },
                                                        label: 'download',
                                                    },
                                                ]}
                                                headline={setTypeTag(item?.debt, item?.amount)}
                                                title={`${t('billDateTitle')} ${getFormatDate(
                                                    item?.issueDate,
                                                    'MM YYYY',
                                                )}`}
                                                description={`${t('billDateDescription')} ${getFormatDate(
                                                    item?.issueDate,
                                                    'DD/MM',
                                                )}`}
                                                extra={
                                                    <>
                                                        <div className='bills-page__desktopWrapper-listBills-extra'>
                                                            <Text6>{formatPrices(item?.amount, 'spent')}</Text6>
                                                            <ButtonLink
                                                                withChevron
                                                                onPress={() => {
                                                                    if (isLight) {
                                                                        dispatch(showSnackbar());
                                                                    } else {
                                                                        sendToNative(
                                                                            '/bills/bill-detail',
                                                                            'modeFlow-backgroundBlue',
                                                                            `${rates[item?.legalEntity]}`,
                                                                            item?.id,
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {t('seeDetails')}
                                                            </ButtonLink>
                                                        </div>
                                                        <a
                                                            ref={downloadRef}
                                                            target={'_blank'}
                                                            className='test-download'
                                                            style={{ visibility: 'hidden', position: 'absolute' }}
                                                        />
                                                    </>
                                                }
                                            />
                                        </div>
                                    );
                                })}
                                <div className='bills-page__download'>
                                    <BoxedRow
                                        onPress={downloadBillsHandler}
                                        asset={
                                            <div
                                                className='bills-page__download-icon'
                                                style={{ backgroundColor: skinVars.colors.brandLow }}
                                            >
                                                <IconInvoicePlanFileRegular color={skinVars.colors.brand} size={24} />
                                            </div>
                                        }
                                        title={t('downloadInvoices')}
                                    />
                                </div>
                                <div className='bills-page__claim-history' style={{ marginBottom: '24px' }}>
                                    <BoxedRow
                                        onPress={claimsHistoryHandler}
                                        asset={
                                            <div
                                                className='bills-page__claim-history-icon'
                                                style={{ backgroundColor: skinVars.colors.brandLow }}
                                            >
                                                <IconInvoiceEuroRegular color={skinVars.colors.brand} size={24} />
                                            </div>
                                        }
                                        title={t('claimHistory')}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <Stack space={16}>
                        <Box padding={size.width >= 1024 ? 0 : 24}>
                            <div className='bills-page__emptyState'>
                                <EmptyState
                                    asset={<IconInvoiceEuroRegular color={skinVars.colors.brand} size={64} />}
                                    title={t('billsEmptyStateNoBillsTitle')}
                                    description={t('billsEmptyStateNoBillsDescription')}
                                ></EmptyState>
                            </div>
                        </Box>
                    </Stack>
                )}
            </div>
        );
    };

    return !billsDisabled ? (
        <div className='bills-page__preclient'>
            {loaded ? (
                <>
                    {!(isWebviewAndroid || isWebviewIOS) && (
                        <div className='bills-page__preclient-header'>
                            <Text6 color={skinVars.colors.textPrimary}>{t('invoice')}</Text6>
                        </div>
                    )}
                    {typeUser !== ETypeUser.PRECLIENT ? (
                        bills()
                    ) : (
                        <div className='bills-page__preclient-emptyState'>
                            <EmptyState
                                asset={<IconInvoiceEuroRegular color={skinVars.colors.brand} size={64} />}
                                title={t('invoice')}
                                description={t('descriptionInvoice')}
                            ></EmptyState>
                        </div>
                    )}
                </>
            ) : (
                <div className='c-spinner'>
                    <Spinner size={24} />
                    <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
                </div>
            )}
        </div>
    ) : (
        <>
            {!(isWebviewAndroid || isWebviewIOS) && (
                <div
                    className='bills-page__preclient-header'
                    style={{
                        background: skinVars.colors.backgroundBrand,
                    }}
                >
                    <Text6 color={skinVars.colors.textPrimaryInverse}>{t('invoice')}</Text6>
                </div>
            )}
            <Stopper></Stopper>
        </>
    );
};

export default Bills;
